import type NanoPopup from "../../components/Popups/NanoPopup";
import type NanoPopupShownEvent from "../../components/Popups/Events/NanoPopupShownEvent";
import type NanoPopupHiddenEvent from "../../components/Popups/Events/NanoPopupHiddenEvent";
import PaywallPopupFactory from "./PaywallPopupFactory";
import { IPaywallPopup } from "./IPaywallPopup";

export type PaywallEnv = {
  licenceType: "FREEMIUM" | "PREMIUM" | null;
  userType: "ROLE_STUDENT" | "ROLE_MANAGER" | null;
};

const readEnvFromBody = (): PaywallEnv => ({
  licenceType: ($("body").attr("data-license-type") ??
    null) as PaywallEnv["licenceType"],
  userType: ($("body").attr("data-user-type") ??
    null) as PaywallEnv["userType"],
});

/**
 * The licence gate: for freemium teachers, clicking any element marked
 * data-paid-feature="1" opens the paid-feature popup instead of the
 * element's action. The DOM attribute is the source of truth and is
 * per-element, dynamic state (BinderMenu toggles it per challenge) — read it
 * with .attr(), never .data() (jQuery's cache goes stale when the attribute
 * is toggled).
 *
 * Production code goes through the statics, which lazily build a singleton
 * from the body attributes; the constructor parameters exist so tests can
 * inject the environment and a popup stub.
 */
export default class Paywall {
  private readonly licenceType: PaywallEnv["licenceType"];
  private readonly userType: PaywallEnv["userType"];
  private readonly createPopup: () => IPaywallPopup;
  private paidFeatureInfoPopup: IPaywallPopup = null;

  constructor(
    env: PaywallEnv = readEnvFromBody(),
    createPopup: () => IPaywallPopup = () => new PaywallPopupFactory().Create(),
    root: JQuery<Document> = $(document),
  ) {
    this.licenceType = env.licenceType;
    this.userType = env.userType;
    this.createPopup = createPopup;

    // The paywall is only relevant if the user is a teacher.
    if (this.licenceType === null || this.userType !== "ROLE_MANAGER") {
      return;
    }

    if (this.licenceType === "FREEMIUM") {
      // Delegated so elements added or toggled after page load (binder menus
      // re-targeted per challenge, popup content) are covered without
      // rebinding.
      root.on("click.freemium", '[data-paid-feature="1"]', (ev) => {
        this.popup.featureName =
          $(ev.currentTarget).attr("data-feature-name") ??
          "default-paid-feature";
        this.popup.Show();
      });
    }

    window.addEventListener("nanopopup-shown", (ev: NanoPopupShownEvent) => {
      if (!ev.paywallPopup) {
        this.currentlyDisplayedPopup = ev.popup;
      }
    });

    window.addEventListener("nanopopup-hidden", (ev: NanoPopupHiddenEvent) => {
      if (!ev.fromPaywall && !ev.paywallPopup) {
        this.currentlyDisplayedPopup = null;
      }
    });
  }

  public static get instance(): Paywall {
    globalThis.paywall ??= new Paywall();
    return globalThis.paywall;
  }

  public static get isFreemium(): boolean {
    return Paywall.instance.isFreemium;
  }

  public static IsLocked(element: JQuery<HTMLElement>): boolean {
    return Paywall.instance.IsLocked(element);
  }

  public get isFreemium(): boolean {
    return this.licenceType === "FREEMIUM" && this.userType === "ROLE_MANAGER";
  }

  /** Whether the element's feature is behind the paywall for this user. */
  public IsLocked(element: JQuery<HTMLElement>): boolean {
    return this.isFreemium && element.attr("data-paid-feature") === "1";
  }

  // The popup the paywall popup covered; TroubadourPaywallPopup restores it
  // when it closes. Paywall popups themselves are never stored here.
  private _currentlyDisplayedPopup: NanoPopup = null;

  public get currentlyDisplayedPopup(): NanoPopup {
    return this._currentlyDisplayedPopup;
  }

  public set currentlyDisplayedPopup(value: NanoPopup) {
    if (value && value.config.isPaywall) {
      return;
    }
    this._currentlyDisplayedPopup = value;
  }

  // Built on first gated click so pages without the popup markup (and tests)
  // never touch the popup classes.
  private get popup(): IPaywallPopup {
    this.paidFeatureInfoPopup ??= this.createPopup();
    return this.paidFeatureInfoPopup;
  }
}
